
import { useState, useEffect } from "react";
import { Navigate, Link, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { PasswordStrengthIndicator } from "@/components/PasswordStrengthIndicator";
import { validatePasswordStrength } from "@/lib/passwordValidation";
import { rateLimiter, getRateLimitMessage } from "@/lib/rateLimiting";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff } from "lucide-react";
import ConfirmEmail from "./ConfirmEmail";
import { CustomerTermsModal } from "@/components/CustomerTermsModal";
import { supabase } from "@/integrations/supabase/client";
import { logger } from '@/lib/logger';
import { trackEvent, FUNNEL_EVENTS } from '@/lib/analytics';

// Check for Supabase invite tokens in hash BEFORE component renders
// This prevents Supabase from auto-processing the tokens
const getInitialInviteState = () => {
  const hash = window.location.hash;
  if (hash) {
    const hashParams = new URLSearchParams(hash.substring(1));
    const type = hashParams.get('type');
    const accessToken = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');

    if (type === 'invite' && accessToken && refreshToken) {
      // Clear the hash immediately to prevent Supabase from processing it
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      return {
        isSupabaseInvite: true,
        tokens: { accessToken, refreshToken }
      };
    }
  }
  return { isSupabaseInvite: false, tokens: null };
};

// Detect a Supabase signup-confirmation link (`#type=signup&...`). Unlike
// the invite handler above, we do NOT strip the hash — Supabase still needs
// the access/refresh tokens to establish the session. We just persist a
// sessionStorage marker so the post-auth navigation can route to /welcome
// instead of the default /.
const SIGNUP_CONFIRMING_KEY = 'fab_signup_confirming';
const detectSignupConfirmation = () => {
  if (typeof window === 'undefined') return;
  const hash = window.location.hash;
  if (!hash) return;
  const hashParams = new URLSearchParams(hash.startsWith('#') ? hash.substring(1) : hash);
  if (hashParams.get('type') === 'signup' && hashParams.get('access_token')) {
    sessionStorage.setItem(SIGNUP_CONFIRMING_KEY, '1');
  }
};
detectSignupConfirmation();

const Auth = () => {
  const { user, signIn, signUp, isLoading } = useAuth();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Check for Supabase invite on initial load (before useEffect)
  const [initialInviteState] = useState(() => getInitialInviteState());

  // Check for mode and email URL params for invite links
  const urlMode = searchParams.get("mode");
  const urlEmail = searchParams.get("email");

  const [email, setEmail] = useState(urlEmail || "");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mode, setMode] = useState<"signin" | "signup">(
    initialInviteState.isSupabaseInvite ? "signup" :
    urlMode === "signup" ? "signup" : "signin"
  );
  const [rateLimitStatus, setRateLimitStatus] = useState<{ blocked: boolean; remainingMs?: number }>({ blocked: false });
  const [showPassword, setShowPassword] = useState(false);
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [signupEmail, setSignupEmail] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Invite-related state
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [isInviteFlow, setIsInviteFlow] = useState(initialInviteState.isSupabaseInvite);
  const [isVerifyingInvite, setIsVerifyingInvite] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  // Supabase native invite flow state
  const [isSupabaseInvite, setIsSupabaseInvite] = useState(initialInviteState.isSupabaseInvite);
  const [supabaseInviteSession, setSupabaseInviteSession] = useState<{ accessToken: string; refreshToken: string } | null>(initialInviteState.tokens);

  // Check for invite tokens in URL on mount
  useEffect(() => {
    // Handle Supabase native invite - get email from tokens
    if (initialInviteState.isSupabaseInvite && initialInviteState.tokens) {
      logger.debug("Auth: Processing Supabase invite tokens");

      // Check if we have email from URL (custom email template)
      const inviteEmail = searchParams.get('invite_email');
      if (inviteEmail) {
        setEmail(inviteEmail);
        logger.debug("Auth: Got email from URL param:", inviteEmail);
      } else {
        // Get email by temporarily setting up session with the tokens
        setIsVerifyingInvite(true);
        supabase.auth.setSession({
          access_token: initialInviteState.tokens.accessToken,
          refresh_token: initialInviteState.tokens.refreshToken,
        }).then(({ data, error }) => {
          if (!error && data.user?.email) {
            setEmail(data.user.email);
            logger.debug("Auth: Got email from Supabase invite session:", data.user.email);
          }
          // Sign out - we want them to complete the form first
          // (they'll set up session again when they submit)
          supabase.auth.signOut();
          setIsVerifyingInvite(false);
        });
      }

      toast({
        title: "Welcome!",
        description: "Please set your password and accept the terms to complete your account setup.",
      });
      return;
    }

    // Check for our custom invite token (query param)
    const customToken = searchParams.get("invite");
    if (customToken) {
      logger.debug("Auth: Found custom invite token in URL, verifying...");
      setIsVerifyingInvite(true);
      setInviteToken(customToken);

      // Verify the invite token
      supabase.functions.invoke("verify-invite", {
        body: { invite_token: customToken }
      }).then(({ data, error }) => {
        if (error || !data?.valid) {
          logger.error("Auth: Invite verification failed:", error || data?.error);
          setInviteError(data?.error || "Invalid or expired invitation link");
          toast({
            variant: "destructive",
            title: "Invalid Invitation",
            description: data?.error || "This invitation link is invalid or has expired.",
          });
        } else {
          logger.debug("Auth: Invite verified successfully:", data.email);
          setEmail(data.email);
          setMode("signup");
          setIsInviteFlow(true);
          toast({
            title: "Welcome!",
            description: `You've been invited to join FAB. Complete the form below to create your account.`,
          });
        }
        setIsVerifyingInvite(false);
      });
    }
  }, [searchParams, toast, initialInviteState]);

  // Check rate limiting on component mount and when email changes
  useEffect(() => {
    if (email) {
      const status = rateLimiter.isBlocked(mode === "signin" ? "login" : "signup", email);
      setRateLimitStatus(status);
    } else {
      setRateLimitStatus({ blocked: false });
    }
  }, [email, mode]);

  // Redirect to home if already logged in (but NOT during invite flow)
  // During invite flow, we want to show the password/TOS form even if session exists.
  // If this is a fresh email-confirmation signup, route through /welcome so the
  // sign_up conversion fires before the onboarding gates. PrivateRoute will
  // still enforce MFA setup/verify before /welcome actually renders.
  if (user && !isLoading && !isSupabaseInvite && !isInviteFlow) {
    const isSignupConfirming = typeof window !== 'undefined'
      && sessionStorage.getItem(SIGNUP_CONFIRMING_KEY) === '1';
    return <Navigate to={isSignupConfirming ? '/welcome' : '/'} replace />;
  }

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    
    // Check rate limiting
    const limitStatus = rateLimiter.isBlocked("login", email);
    if (limitStatus.blocked) {
      toast({
        variant: "destructive",
        title: "Too many attempts",
        description: getRateLimitMessage("login", limitStatus.remainingMs || 0),
      });
      return;
    }
    
    setIsSubmitting(true);
    try {
      await signIn(email, password);
      // Record successful attempt
      rateLimiter.recordAttempt("login", email, true);
    } catch (error) {
      // Record failed attempt
      rateLimiter.recordAttempt("login", email, false);
      
      // Update rate limit status
      const newStatus = rateLimiter.isBlocked("login", email);
      setRateLimitStatus(newStatus);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;

    // Check if terms are accepted
    if (!acceptedTerms) {
      toast({
        variant: "destructive",
        title: "Terms acceptance required",
        description: "Please accept the Customer Terms of Service to create an account.",
      });
      return;
    }

    // Check rate limiting
    const limitStatus = rateLimiter.isBlocked("signup", email);
    if (limitStatus.blocked) {
      toast({
        variant: "destructive",
        title: "Too many attempts",
        description: getRateLimitMessage("signup", limitStatus.remainingMs || 0),
      });
      return;
    }

    // Validate password strength
    const passwordValidation = validatePasswordStrength(password);
    if (!passwordValidation.isValid) {
      toast({
        variant: "destructive",
        title: "Password requirements not met",
        description: "Please ensure your password meets all security requirements shown below.",
      });
      return;
    }

    trackEvent(FUNNEL_EVENTS.SIGNUP_START, { invite: isInviteFlow || isSupabaseInvite ? true : false });

    setIsSubmitting(true);
    try {
      // Handle Supabase native invite flow differently
      if (isSupabaseInvite && supabaseInviteSession) {
        logger.debug("Auth: Completing Supabase invite flow");

        // 1. Set up the session with the invite tokens
        const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
          access_token: supabaseInviteSession.accessToken,
          refresh_token: supabaseInviteSession.refreshToken,
        });

        if (sessionError || !sessionData.user) {
          throw new Error(sessionError?.message || "Failed to establish session");
        }

        // 2. Update the user's password
        const { error: updateError } = await supabase.auth.updateUser({ password });
        if (updateError) {
          throw new Error(updateError.message);
        }

        // 3. Update user metadata with TOS acceptance
        const { error: metadataError } = await supabase.auth.updateUser({
          data: {
            accepted_customer_tos_version: "2025-10-24",
            accepted_customer_tos_at: new Date().toISOString(),
          }
        });
        if (metadataError) {
          logger.error("Auth: Failed to update TOS metadata:", metadataError);
        }

        // 4. Record TOS acceptance in audit table
        logger.debug("Auth: Recording TOS acceptance for Supabase invite user");
        const { error: tosError } = await supabase.functions.invoke("record-tos-acceptance", {
          body: {
            tos_version: "2025-10-24",
            tos_type: "customer_tos",
            metadata: {
              signup_flow: true,
              invite_flow: true,
              supabase_invite: true,
            }
          }
        });

        if (tosError) {
          logger.error("Auth: Failed to record TOS in audit table:", tosError);
        } else {
          // Mark as recorded in user metadata
          await supabase.auth.updateUser({
            data: { tos_recorded_in_audit: true }
          });
        }

        trackEvent(FUNNEL_EVENTS.SIGNUP_SUCCESS, { flow: "invite" });

        toast({
          title: "Account setup complete!",
          description: "Welcome! Your account is ready to use.",
        });

        // Navigate to MFA setup (required before tier selection)
        // Use React Router navigate to avoid full page reload
        navigate('/mfa/setup');
        return;
      }

      // Standard signup flow (non-invite or custom invite)
      const result = await signUp(email, password, acceptedTerms);
      // Record successful attempt
      rateLimiter.recordAttempt("signup", email, true);

      // If this was a custom invite flow, mark the invite as used
      if (isInviteFlow && inviteToken && result.user) {
        logger.debug("Auth: Marking custom invite as used after successful signup");
        supabase.functions.invoke("verify-invite", {
          body: { invite_token: inviteToken, mark_used: true }
        }).catch((err) => {
          logger.error("Auth: Failed to mark invite as used:", err);
          // Don't fail the signup if this fails
        });
      }

      // Check if email confirmation is required
      // If no session/user returned, show email confirmation screen
      trackEvent(FUNNEL_EVENTS.SIGNUP_SUCCESS, { flow: "standard" });
      if (!result.session || !result.user) {
        setSignupEmail(email);
        setShowEmailConfirmation(true);
      }
      // If user is immediately logged in, AuthContext will handle the redirect
    } catch (error) {
      // Record failed attempt
      rateLimiter.recordAttempt("signup", email, false);

      // Update rate limit status
      const newStatus = rateLimiter.isBlocked("signup", email);
      setRateLimitStatus(newStatus);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading || isVerifyingInvite) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Show email confirmation screen after signup
  if (showEmailConfirmation) {
    return <ConfirmEmail email={signupEmail} />;
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold">
            {isInviteFlow ? "Welcome to FAB!" : mode === "signin" ? "Log in" : "Create an Account"}
          </CardTitle>
          <CardDescription>
            {isInviteFlow ? (
              <>Complete your account setup to get started.</>
            ) : mode === "signin" ? (
              <>
                Don't have an account?{" "}
                <button
                  type="button"
                  onClick={() => setMode("signup")}
                  className="text-primary hover:underline font-medium"
                >
                  Create an Account
                </button>
              </>
            ) : (
              <>
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => setMode("signin")}
                  className="text-primary hover:underline font-medium"
                >
                  Log in
                </button>
              </>
            )}
          </CardDescription>
        </CardHeader>

        {/* Rate Limit Warning */}
        {rateLimitStatus.blocked && (
          <Alert className="mx-6 mb-4" variant="destructive">
            <AlertDescription>
              {getRateLimitMessage(
                mode === "signin" ? "login" : "signup",
                rateLimitStatus.remainingMs || 0
              )}
            </AlertDescription>
          </Alert>
        )}

        {mode === "signin" ? (
          <form onSubmit={handleSignIn}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <div className="text-right">
                  <Link
                    to="/auth/forgot-password"
                    className="text-sm text-primary hover:underline"
                  >
                    Forgot Password?
                  </Link>
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Button
                type="submit"
                className="w-full"
                disabled={isSubmitting || rateLimitStatus.blocked}
              >
                {isSubmitting ? "Signing in..." : "Log in"}
              </Button>
            </CardFooter>
          </form>
        ) : (
          <form onSubmit={handleSignUp}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email-signup">Email Address</Label>
                <Input
                  id="email-signup"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  readOnly={isInviteFlow}
                  className={isInviteFlow ? "bg-muted cursor-not-allowed" : ""}
                />
                {isInviteFlow && (
                  <p className="text-xs text-muted-foreground">
                    This email was set by your invitation and cannot be changed.
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="password-signup">Password</Label>
                <div className="relative">
                  <Input
                    id="password-signup"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <PasswordStrengthIndicator
                  password={password}
                  className="mt-2"
                />
              </div>
              <div className="flex items-start space-x-2 pt-2">
                <Checkbox
                  id="terms-acceptance"
                  checked={acceptedTerms}
                  onCheckedChange={(checked) => setAcceptedTerms(checked === true)}
                  className="mt-0.5"
                />
                <div className="space-y-0 leading-none">
                  <Label
                    htmlFor="terms-acceptance"
                    className="text-sm font-normal cursor-pointer"
                  >
                    I agree to the{" "}
                    <CustomerTermsModal>
                      <button
                        type="button"
                        className="text-primary hover:underline font-medium"
                      >
                        Customer Terms of Service
                      </button>
                    </CustomerTermsModal>
                  </Label>
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Button
                type="submit"
                className="w-full"
                disabled={isSubmitting || rateLimitStatus.blocked || !validatePasswordStrength(password).isValid || !acceptedTerms}
              >
                {isSubmitting ? "Creating account..." : "Create Account"}
              </Button>
            </CardFooter>
          </form>
        )}
      </Card>
    </div>
  );
};

export default Auth;
